/**
 * "Your work" on the activity feed: a few friendly pointers to what is waiting for you (sections, to-dos,
 * reviewer comments, calls closing soon, invitations, meeting polls), each linking to it. Only what is real:
 * with nothing waiting there is no card. "Not now" puts it away for a few days.
 * Backend: GET /users/me/nudges/ (registry/user/nudges.py).
 */
import React, { useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ArrowForward as GoIcon,
  Article as SectionIcon,
  TaskAlt as TodoIcon,
  RateReview as ReviewIcon,
  Campaign as CallIcon,
  Event as EventIcon,
  HowToVote as PollIcon,
  WbSunny as SunIcon,
} from '@mui/icons-material';
import { fetcher } from 'shared/helpers/axios';

const SNOOZE_KEY = 'otable.yourwork.snoozedUntil';
const SNOOZE_DAYS = 3;
const snoozed = () => { try { return Number(localStorage.getItem(SNOOZE_KEY) || 0) > Date.now(); } catch { return false; } };

const ICONS = { sections: SectionIcon, todos: TodoIcon, reviews: ReviewIcon, calls: CallIcon, events: EventIcon, polls: PollIcon };
const iconFor = (key) => ICONS[key.split('-')[0]] || GoIcon;

export default function YourWorkCard() {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(snoozed);
  const { data } = useSWR(hidden ? null : '/users/me/nudges/', fetcher, { revalidateOnFocus: false });
  const items = Array.isArray(data) ? data : [];
  if (hidden || items.length === 0) return null;

  const notNow = () => {
    try { localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000)); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <Card elevation={0} sx={{ borderRadius: '12px', border: '1px solid', borderColor: (t) => alpha(t.palette.primary.main, 0.25),
      bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
      <CardContent sx={{ pb: '12px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <SunIcon sx={{ color: 'warning.main', fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>Your work</Typography>
        </Box>
        <Stack spacing={0.5}>
          {items.map((item) => {
            const Icon = iconFor(item.key);
            return (
              <Box key={item.key} onClick={() => navigate(item.url)} role="link" tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(item.url); }}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75, borderRadius: 1.5, cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' }, '&:hover .go': { opacity: 1 } }}>
                <Icon sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{item.text}</Typography>
                <GoIcon className="go" sx={{ fontSize: 16, color: 'text.secondary', opacity: 0, transition: 'opacity 0.15s' }} />
              </Box>
            );
          })}
        </Stack>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
          <Button size="small" onClick={notNow} sx={{ textTransform: 'none', color: 'text.secondary' }}>Not now</Button>
        </Box>
      </CardContent>
    </Card>
  );
}
